import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Select from '../../../../components/ui/Select';
import SkillAutocomplete from './SkillAutocomplete';
import { createSkill } from '../../../../api/Employee/skills';
import { ProficiencyLevel, SkillView } from '../../../../models/employee';

interface SuggestSkillModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { skillName: string; proficiency: ProficiencyLevel; rationale?: string }) => void;
  /**
   * Failure from the PARENT's save call (`addUserSkill`) — the step this modal cannot see.
   * `createError` below only covers creating the skill in the company catalogue. See the matching
   * note on `AddSkillModal`: a rejected save used to close this modal with no message at all.
   */
  submitError?: string | null;
}

const SuggestSkillModal: React.FC<SuggestSkillModalProps> = ({ isOpen, onClose, onSubmit, submitError }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    skillName: '',
    proficiency: ProficiencyLevel.Awareness,
    rationale: ''
  });

  const [createError, setCreateError] = useState('');

  const proficiencyOptions = [
    { value: ProficiencyLevel.Awareness, label: t('Awareness') },
    { value: ProficiencyLevel.Working, label: t('Working') },
    { value: ProficiencyLevel.Strong, label: t('Strong') }
  ];

  // A name nobody has used yet is the normal case here, so the field offers to add it rather than
  // leaving the user to guess whether typing it was enough.
  const handleCreateSkill = async (name: string): Promise<SkillView | null> => {
    setCreateError('');
    try {
      const result = await createSkill({ name });
      if (result.errors.length > 0 || !result.payload) {
        setCreateError(result.errors[0]?.description || t('skills.createSkillFailed'));
        return null;
      }
      return result.payload;
    } catch {
      setCreateError(t('skills.createSkillFailed'));
      return null;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e?.preventDefault();
    if (formData?.skillName) {
      onSubmit({
        skillName: formData.skillName,
        proficiency: formData.proficiency,
        rationale: formData.rationale
      });
      // Deliberately NOT cleared here — the parent's save can fail, and wiping the fields
      // optimistically is what made a rejected save look like a no-op with the input gone.
      // The parent unmounts this modal on success, which resets the state.
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Icon name="Award" size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{t('Add Skill')}</h2>
              <p className="text-sm text-muted-foreground">{t('Add a skill to your profile')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <SkillAutocomplete
            label={t('Skill Name')}
            placeholder={t('Search or enter a skill...')}
            value={formData?.skillName}
            onChange={(value) => setFormData({ ...formData, skillName: value })}
            onCreate={handleCreateSkill}
            required
          />
          {createError && (
            <p className="text-sm text-destructive" data-testid="skill-create-error">{createError}</p>
          )}

          <Select
            label={t('Proficiency Level')}
            options={proficiencyOptions}
            value={formData?.proficiency}
            onChange={(value) => setFormData({ ...formData, proficiency: value as ProficiencyLevel })}
            required
          />

          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              <strong>{t('Proficiency Levels')}:</strong><br />
              • <strong>{t('Awareness')}:</strong> {t('Basic understanding')}<br />
              • <strong>{t('Working')}:</strong> {t('Can apply independently')}<br />
              • <strong>{t('Strong')}:</strong> {t('Advanced capability')}
            </p>
          </div>

          {submitError && (
            <div
              className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive"
              data-testid="skill-submit-error"
              role="alert"
            >
              {submitError}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Plus"
            >
              {t('Add Skill')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SuggestSkillModal;
